import React, { useEffect, useState, useMemo } from "react";
import { Modal, Tabs, Descriptions, Tag, Typography, Empty } from "antd";
import { TruckOutlined, FileTextOutlined, CheckCircleOutlined } from "@ant-design/icons";
import type { DispatchResponse, DispatchedItemResponse } from "@OrianaTypes";
import { useGetEntityFilesQuery, FileUploadResponse } from "../../store/api/fileApi";
import UploadedFilesList from "./UploadedFilesList";
import { formatLabel, getStatusColorByType } from "../../utils";

const { Text } = Typography;

export type DispatchDetailsTab = "dispatch" | "documents" | "delivery";

interface DispatchDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  dispatch: DispatchResponse | null;
  initialTab?: DispatchDetailsTab;
}

const DispatchDetailsModal: React.FC<DispatchDetailsModalProps> = ({
  visible,
  onClose,
  dispatch,
  initialTab = "dispatch",
}) => {
  const [activeTab, setActiveTab] = useState<DispatchDetailsTab>(initialTab);

  // Fetch files for dispatch documents
  const { data: dispatchDocResponse, isLoading: isLoadingDispatchDocs } = useGetEntityFilesQuery(
    { entityType: "dispatch_document", entityId: dispatch?.dispatchId?.toString() || "" },
    { skip: !dispatch?.dispatchId || !visible }
  );

  // Fetch files for delivery confirmation
  const { data: deliveryDocResponse, isLoading: isLoadingDeliveryDocs } = useGetEntityFilesQuery(
    { entityType: "delivery_confirmation", entityId: dispatch?.dispatchId?.toString() || "" },
    { skip: !dispatch?.dispatchId || !visible }
  );

  // Extract file arrays from responses
  const dispatchDocFiles: FileUploadResponse[] = useMemo(
    () => dispatchDocResponse?.data || [],
    [dispatchDocResponse]
  );
  const deliveryDocFiles: FileUploadResponse[] = useMemo(
    () => deliveryDocResponse?.data || [],
    [deliveryDocResponse]
  );

  // Update active tab when initialTab changes
  useEffect(() => {
    if (visible) {
      setActiveTab(initialTab);
    }
  }, [visible, initialTab]);

  // Dispatch Details Tab Content
  const renderDispatchDetails = () => {
    if (!dispatch) return <Empty description="No data available" />;

    return (
      <Descriptions
        bordered
        column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
        size="small"
        labelStyle={{
          fontWeight: 600,
          backgroundColor: "#fafafa",
          width: "180px",
        }}
      >
        <Descriptions.Item label="Dispatch ID">
          <Tag color="blue">#{dispatch.dispatchId || "-"}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Confirm Dispatch Date">
          {dispatch.confirmDispatchDate || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Project Name">
          {dispatch.projectName || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Project Location">
          {dispatch.projectLocation || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Material Dispatch From Location">
          {dispatch.deliveryLocation || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Delivery Address">
          {dispatch.deliveryAddress || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Google Map Link">
          {dispatch.googleMapLink ? (
            <a
              href={dispatch.googleMapLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1890ff" }}
            >
              View Map
            </a>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Delivery Contact">
          {dispatch.deliveryContact || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Dispatched Items" span={2}>
          {dispatch.dispatchedItems && dispatch.dispatchedItems.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
              {dispatch.dispatchedItems.map(
                (item: DispatchedItemResponse, index: number) => (
                  <Tag key={index} color="geekblue">
                    {formatLabel(item.productName || "")}: {item.quantity}
                  </Tag>
                )
              )}
            </div>
          ) : (
            "-"
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Remarks" span={2}>
          {dispatch.remarks || "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Created At">
          {dispatch.createdAt
            ? new Date(dispatch.createdAt).toLocaleString()
            : "-"}
        </Descriptions.Item>
        <Descriptions.Item label="Created By">
          {dispatch.createdBy || "-"}
        </Descriptions.Item>
      </Descriptions>
    );
  };

  // Dispatch Documents Tab Content
  const renderDocumentDetails = () => {
    if (!dispatch) return <Empty description="No data available" />;

    const hasDocumentData =
      dispatch.dispatchStatus || dispatch.taxInvoiceNumber || dispatch.ewayBill;

    if (!hasDocumentData) {
      return <Empty description="No document details available" />;
    }

    return (
      <div>
        <Descriptions
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          size="small"
          labelStyle={{
            fontWeight: 600,
            backgroundColor: "#fafafa",
            width: "180px",
          }}
          style={{ marginBottom: "16px" }}
        >
          <Descriptions.Item label="Dispatch ID">
            <Tag color="blue">#{dispatch.dispatchId || "-"}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="No Due Clearance">
            {dispatch.noDuesClearance ? (
              <Tag
                color={getStatusColorByType(
                  dispatch.noDuesClearance,
                  "clearance"
                )}
              >
                {formatLabel(dispatch.noDuesClearance)}
              </Tag>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="OSG PI No">
            {dispatch.docOsgPiNo || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="OSG PI Date">
            {dispatch.docOsgPiDate || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Tax Invoice No">
            {dispatch.taxInvoiceNumber || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Invoice Date">
            {dispatch.invoiceDate || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="E-way Bill">
            {dispatch.ewayBill || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Delivery Challan">
            {dispatch.deliveryChallan || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Dispatch Date">
            {dispatch.dispatchDate || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Dispatch Status">
            {dispatch.dispatchStatus ? (
              <Tag
                color={getStatusColorByType(
                  dispatch.dispatchStatus,
                  "dispatch"
                )}
              >
                {formatLabel(dispatch.dispatchStatus)}
              </Tag>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Dispatch LR No">
            {dispatch.dispatchLrNo || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Material Dispatch From Location">
            {dispatch.deliveryPincode || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Packaging List" span={2}>
            {dispatch.packagingList || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Dispatched Items" span={2}>
            {dispatch.dispatchedItems && dispatch.dispatchedItems.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", maxWidth: "100%" }}>
                {dispatch.dispatchedItems.map(
                  (item: DispatchedItemResponse, index: number) => (
                    <Tag
                      key={index}
                      color="geekblue"
                      style={{
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        lineHeight: "1.5"
                      }}
                    >
                      <span style={{ display: "block" }}>
                        {formatLabel(item.productName || "")}: {item.quantity}
                      </span>
                      {item.serialNumbers && (
                        <Text
                          type="secondary"
                          style={{
                            fontSize: "12px",
                            display: "block",
                            wordBreak: "break-word",
                            overflowWrap: "break-word"
                          }}
                        >
                          (S/N: {item.serialNumbers})
                        </Text>
                      )}
                    </Tag>
                  )
                )}
              </div>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Remarks" span={2}>
            {dispatch.dispatchRemarks || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {dispatch.documentUpdatedAt
              ? new Date(dispatch.documentUpdatedAt).toLocaleString()
              : "-"}
          </Descriptions.Item>
        </Descriptions>

        <UploadedFilesList
          files={dispatchDocFiles}
          isLoading={isLoadingDispatchDocs}
          title="Uploaded Documents"
        />
      </div>
    );
  };

  // Delivery Confirmation Tab Content
  const renderDeliveryDetails = () => {
    if (!dispatch) return <Empty description="No data available" />;

    const hasDeliveryData = dispatch.deliveryStatus || dispatch.dateOfDelivery;

    if (!hasDeliveryData) {
      return <Empty description="No delivery confirmation details available" />;
    }

    return (
      <div>
        <Descriptions
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
          size="small"
          labelStyle={{
            fontWeight: 600,
            backgroundColor: "#fafafa",
            width: "180px",
          }}
        >
          <Descriptions.Item label="Dispatch ID">
            <Tag color="blue">#{dispatch.dispatchId || "-"}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Delivery Status">
            {dispatch.deliveryStatus ? (
              <Tag
                color={getStatusColorByType(dispatch.deliveryStatus, "delivery")}
              >
                {formatLabel(dispatch.deliveryStatus)}
              </Tag>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Date of Delivery">
            {dispatch.dateOfDelivery || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Proof of Delivery">
            {dispatch.proofOfDelivery || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Dispatched Items" span={2}>
            {dispatch.dispatchedItems && dispatch.dispatchedItems.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", maxWidth: "100%" }}>
                {dispatch.dispatchedItems.map(
                  (item: DispatchedItemResponse, index: number) => (
                    <Tag
                      key={index}
                      color="geekblue"
                      style={{
                        maxWidth: "100%",
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        lineHeight: "1.5"
                      }}
                    >
                      <span style={{ display: "block" }}>
                        {formatLabel(item.productName || "")}: {item.quantity}
                      </span>
                      {item.serialNumbers && (
                        <Text
                          type="secondary"
                          style={{
                            fontSize: "12px",
                            display: "block",
                            wordBreak: "break-word",
                            overflowWrap: "break-word"
                          }}
                        >
                          (S/N: {item.serialNumbers})
                        </Text>
                      )}
                    </Tag>
                  )
                )}
              </div>
            ) : (
              "-"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {dispatch.deliveryUpdatedAt
              ? new Date(dispatch.deliveryUpdatedAt).toLocaleString()
              : "-"}
          </Descriptions.Item>
        </Descriptions>

        <UploadedFilesList
          files={deliveryDocFiles}
          isLoading={isLoadingDeliveryDocs}
          title="Proof of Delivery Documents"
        />
      </div>
    );
  };

  const tabItems = [
    {
      key: "dispatch",
      label: (
        <span>
          <TruckOutlined />
          Dispatch Details
        </span>
      ),
      children: renderDispatchDetails(),
    },
    {
      key: "documents",
      label: (
        <span>
          <FileTextOutlined />
          Dispatch Documents
        </span>
      ),
      children: renderDocumentDetails(),
    },
    {
      key: "delivery",
      label: (
        <span>
          <CheckCircleOutlined />
          Delivery Confirmation
        </span>
      ),
      children: renderDeliveryDetails(),
    },
  ];

  return (
    <Modal
      title={`Dispatch Details - #${dispatch?.dispatchId || ""}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as DispatchDetailsTab)}
        items={tabItems}
        style={{ marginTop: "8px" }}
      />
    </Modal>
  );
};

export default DispatchDetailsModal;
